import { ApiService } from '@sparklayer-io/spark-dash-core/types/api';
import { PLAN_KEYS } from '../constants';
import type {
  AvailablePlanResponse,
  BillingDataResponse,
  InitiateFlowResponse,
} from '../types/api';
import type { BillingData, BillingPlan } from '../types/plan';

export interface BillingApiContract {
  availablePlans(siteId: string): Promise<BillingPlan[]>;
  data(siteId: string): Promise<BillingData>;
  initiateFlow(siteId: string, planKey: string): Promise<InitiateFlowResponse>;
  assignDevelopmentPlan(siteId: string): Promise<void>;
  assignEnterpriseOnboardingPlan(siteId: string): Promise<void>;
}

declare module '@sparklayer-io/spark-dash-core/context/page-context' {
  interface Apis {
    billing: BillingApiContract;
  }
}

/**
 * A service to interact with team billing via the dashboard API.
 *
 * Note: billing endpoints live on the dashboard backend, so this service uses
 * `apiHandler.fetch` (NOT `apiHandler.proxy`, which targets spark-api).
 */
export class BillingApi extends ApiService implements BillingApiContract {
  public async availablePlans(siteId: string): Promise<BillingPlan[]> {
    const response = await this.api.fetch<AvailablePlanResponse[]>({
      method: 'GET',
      path: `/team/${encodeURIComponent(siteId)}/billing/available-plans`,
    });
    return response.map((plan) => ({
      planKey: plan.plan_key,
      name: plan.name,
      subName: plan.sub_name,
      description: plan.description,
      price: plan.plan_key === PLAN_KEYS.enterprise ? null : Number(plan.price),
      points: plan.points,
    }));
  }

  public async data(siteId: string): Promise<BillingData> {
    const response = await this.api.fetch<BillingDataResponse>({
      method: 'GET',
      path: `/team/${encodeURIComponent(siteId)}/billing/data`,
    });

    return {
      provider: response.provider,
      activePlanKey: response.active_plan_key,
      allowChangePlan: response.allow_change_plan,
    };
  }

  public initiateFlow(siteId: string, planKey: string): Promise<InitiateFlowResponse> {
    return this.api.fetch<InitiateFlowResponse>({
      method: 'GET',
      path: `/team/${encodeURIComponent(siteId)}/billing/initiate-flow/${planKey}`,
    });
  }

  public async assignDevelopmentPlan(siteId: string): Promise<void> {
    await this.api.fetch<void>({
      method: 'POST',
      path: `/team/${encodeURIComponent(siteId)}/billing/assign-development-plan`,
    });
  }

  public async assignEnterpriseOnboardingPlan(siteId: string): Promise<void> {
    await this.api.fetch<void>({
      method: 'POST',
      path: `/team/${encodeURIComponent(siteId)}/billing/assign-enterprise-onboarding-plan`,
    });
  }
}
